import { RefObject, useEffect } from 'react';

import type { Series, YAxisPlotLinesOptions } from 'highcharts';

import { StructuredMeasurements } from '@restDataAccess';
import { MeasurementRange } from '@scaleaq/sitecontrol-lib';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import {
  aggregatedAreaColors,
  AggregatedChartConfigType,
  ChartAggregationResolution,
  dateTimeLabelFormats,
  defaultChartDateFormat,
  defaultChartDateTimeFormat,
  defaultTooltipOptions,
  defaultXAxisOptions,
  getDefaultTooltipPointFormatter,
  getLocaleFromLanguage,
  getMarkerSymbol,
  lineWidth,
  markerEnabledThreshold,
  markerRadius,
  referenceLineColor,
  updateChartLocale,
} from '@/components';
import Highcharts from '@/components/chart/highcharts';

import 'highcharts/esm/highcharts-more.js';

import { Z_INDEX } from '@/constants';
import { useEventClassUnitPair, useGetFriendlyProviderName } from '@/hooks';
import { colors, fromApiDateTime, round, roundToTwoDecimals } from '@/utils';

import {
  setAxisPlotLines,
  syncChartYAxes,
} from '../../../chartHelperFunctions/configureChartYAxes.ts';
import { getAggregatedChartXAxisWindow } from '../../../chartHelperFunctions/getAggregatedChartXAxisWindow.ts';
import { useChartZoom } from '../../chartZoom/useChartZoom.ts';
import { hasApiDayBuckets } from './chartMeasurements.functions.ts';
import { useLowDataCapture } from './useLowDataCapture.tsx';

const referenceLineId = 'reference';
const lowDataCaptureId = 'lowDataCapture';
const lowDataCaptureLegendId = 'lowDataCaptureLegend';
const confidenceIntervalOpacity = 0.25;
const standardDeviationAreaOpacity = 0.5;
const errorBarLineWidth = 3;
const defaultXAxisLabelDistance = 15;
const xAxisLabelDistanceWithLowDataCapture = 25;
const lowDataCaptureRadius = 10;
const ONE_DAY_MS = 24 * 60 * 60 * 1000;
const zScore = 1.96;

interface UseConfigureAggregatedHighChartProps {
  allMeasurements: StructuredMeasurements<MeasurementRange> | undefined;
  config: AggregatedChartConfigType;
  hasMeasurements: boolean;
  highChartsRef: RefObject<HighchartsReact.RefObject | null>;
  isLive: boolean;
  isTimeSeriesNormalized?: boolean;
  ceiling: number | undefined;
  floor: number | undefined;
  lowDataCaptureThreshold?: number;
  maxPadding: number | undefined;
  minRange: number | undefined;
  minorTickInterval: number | undefined;
  tickInterval: number | undefined;
}

export const useConfigureAggregatedHighChart = ({
  allMeasurements,
  config: {
    resolution,
    colors: chartColors,
    from,
    hiddenPlotTypes,
    interval,
    plot,
    reference,
    sensors,
    to,
  },
  ceiling,
  floor,
  hasMeasurements,
  highChartsRef,
  isLive,
  isTimeSeriesNormalized = false,
  lowDataCaptureThreshold,
  maxPadding,
  minRange,
  minorTickInterval,
  tickInterval,
}: UseConfigureAggregatedHighChartProps) => {
  const { t, i18n } = useTranslation(['chartConfig']);
  const { units } = useEventClassUnitPair();
  const { applyBaseRange } = useChartZoom(highChartsRef);
  const lowDataCapturePoints = useLowDataCapture(lowDataCaptureThreshold, allMeasurements);

  const { getFriendlyProviderName } = useGetFriendlyProviderName();

  useEffect(() => {
    const plotAsAStringArray = plot.map(p => p.value);

    const chart = highChartsRef.current?.chart;
    const isRange = sensors.length === 1 && plot.length === 1;
    const hasLowDataCapture = lowDataCapturePoints.length > 0;

    while (chart?.series.length) {
      chart.series[0]?.remove();
    }

    const locale = getLocaleFromLanguage(i18n.language);

    // Set locale both globally and on the chart instance to ensure it's applied
    // This is critical because existing chart instances may not pick up global setOptions changes
    Highcharts.setOptions({
      lang: {
        locale: locale,
      },
    });

    // Update the chart instance's lang property directly to ensure locale is applied
    updateChartLocale(chart, locale);

    chart?.xAxis[0]?.update({
      ...defaultXAxisOptions,
      dateTimeLabelFormats,
      // Daily points sit at midnight. Without a floor, Highcharts can pick 12-hour ticks (for
      // example once the low data capture series is added), giving "12:00" labels between dates.
      minTickInterval: resolution === ChartAggregationResolution.DAILY ? ONE_DAY_MS : undefined,
      labels: {
        distance: hasLowDataCapture
          ? xAxisLabelDistanceWithLowDataCapture
          : defaultXAxisLabelDistance,
      },
    });

    const plotLineOptions: YAxisPlotLinesOptions = {
      id: referenceLineId,
      value: reference?.value,
      label: {
        text: reference?.label,
        style: { color: referenceLineColor },
        x: 0,
      },
      color: referenceLineColor,
      dashStyle: 'Dash',
      zIndex: Z_INDEX.base.elevated,
    };

    const plotTypeToYAxisIndex = syncChartYAxes(chart, plotAsAStringArray, units, {
      plotLines: reference ? [plotLineOptions] : undefined,
      primaryAxisOverrides: {
        tickInterval,
        minorTicks: Boolean(minorTickInterval),
        minorTickInterval,
        minRange,
        ceiling,
        floor,
        ...(maxPadding === undefined ? {} : { maxPadding }),
      },
    });

    chart?.legend.update({
      events: {
        itemClick: function (e) {
          const clickedSeries = e.legendItem as Series;
          const clickedSeriesId = clickedSeries.options.id;
          if (clickedSeriesId === referenceLineId) {
            setAxisPlotLines(this.chart.yAxis[0], clickedSeries.visible ? [] : [plotLineOptions]);
          }
          if (clickedSeriesId === lowDataCaptureLegendId) {
            const lowDataCaptureSeries = this.chart.series.find(
              s => s.options.id === lowDataCaptureId
            );
            if (lowDataCaptureSeries?.visible) lowDataCaptureSeries.hide();
            else lowDataCaptureSeries?.show();
          }
        },
      },
    });

    if (hasMeasurements) {
      if (reference) {
        chart?.addSeries({
          id: referenceLineId,
          type: 'line',
          dashStyle: 'Dash',
          color: colors.stroke.warning.solid[100],
          name: `${reference.label || t('chartConfig:linear.labels.reference')}: ${reference.value}`,
          marker: {
            enabled: false,
          },
          lineWidth: lineWidth,
        });
      }

      if (hasLowDataCapture) {
        chart?.addSeries({
          id: lowDataCaptureId,
          type: 'scatter',
          color: colors.stroke.caution.solid['100'],
          marker: {
            symbol: 'lowDataCapture',
            radius: lowDataCaptureRadius,
          },
          showInLegend: false,
          data: lowDataCapturePoints,
          enableMouseTracking: false,
        });

        // To properly display icon in legend
        chart?.addSeries({
          id: lowDataCaptureLegendId,
          type: 'scatter',
          color: colors.stroke.caution.solid['100'],
          name: t('linear.lowDataCapture'),
          marker: {
            symbol: 'lowDataCaptureLegend',
            radius: lowDataCaptureRadius,
          },
        });
      }

      let seriesIndex = 0;

      /**
       * Generate line name for each series
       */
      const sensorAsStringArray = sensors.map(s => s.value);
      const sensorNames = sensors.reduce(
        (acc, sensor) => ({
          ...acc,
          [sensor.value]: sensor.label,
        }),
        {} as Record<string, string>
      );
      const getLineName = (sensorId: string, provider: string) =>
        sensorAsStringArray.length > 1
          ? (sensorNames[sensorId] ?? sensorId)
          : getFriendlyProviderName(provider);

      Object.entries(allMeasurements ?? {}).forEach(([plotType, measurements]) =>
        Object.entries(measurements).forEach(([sensorId, plotMeasurements]) => {
          const unit = units[plotType];
          const color = chartColors?.[plotType] || chartColors?.[sensorId] || undefined;
          const seriesId = uuidv4();
          chart?.addSeries({
            name: isRange ? t('chartConfig:linear.mean') : getLineName(sensorId, plotType),
            id: seriesId,
            color,
            visible: !hiddenPlotTypes?.includes(plotType),
            lineWidth: isRange ? 2 : lineWidth,
            type: 'spline',
            yAxis: plotTypeToYAxisIndex[plotType],
            data: plotMeasurements.map(m => [fromApiDateTime(m.timestamp).toMillis(), m.mean]),
            zIndex: Z_INDEX.base.chartSeries,
            tooltip: {
              ...defaultTooltipOptions,
              xDateFormat: hasApiDayBuckets(resolution)
                ? defaultChartDateFormat
                : defaultChartDateTimeFormat,
              pointFormatter: getDefaultTooltipPointFormatter(unit),
            },
            marker: {
              enabled: false,
              states: { hover: { enabled: false } },
              enabledThreshold: markerEnabledThreshold,
              radius: markerRadius,
              symbol: getMarkerSymbol(seriesIndex++),
              lineWidth: 0,
            },
          });
          if (isRange) {
            // Standard deviation area chart
            chart?.addSeries({
              name: t('chartConfig:linear.standardDeviation'),
              type: 'areasplinerange',
              visible: false,
              color: (chart?.get(seriesId) as Series)?.color || aggregatedAreaColors[0],
              opacity: standardDeviationAreaOpacity,
              data: plotMeasurements.map(m => ({
                x: fromApiDateTime(m.timestamp).toMillis(),
                low: m.mean - (m.std ?? 0),
                high: m.mean + (m.std ?? 0),
                // use std as name to show in tooltip
                name: m.std || m.std === 0 ? `${roundToTwoDecimals(m.std)}` : '-',
              })),
              lineWidth: lineWidth,
              linkedTo: seriesId,
              zIndex: Z_INDEX.base.chart,
              tooltip: {
                ...defaultTooltipOptions,
                xDateFormat: hasApiDayBuckets(resolution)
                  ? defaultChartDateFormat
                  : defaultChartDateTimeFormat,
                pointFormatter: function () {
                  return `${t('chartConfig:linear.standardDeviation')}: <strong>${this.name !== undefined ? this.name : ''}</strong> ${unit ?? ''}<br>`;
                },
              },
              marker: {
                enabledThreshold: markerEnabledThreshold,
                radius: 3,
                symbol: 'circle',
                lineWidth: 0,
                enabled: false,
              },
              showInLegend: true,
            });

            // Confidence interval area chart
            chart?.addSeries({
              name: t('linear.CI'),
              type: 'areasplinerange',
              color: aggregatedAreaColors[1],
              opacity: confidenceIntervalOpacity,
              data: plotMeasurements.map(m => ({
                x: fromApiDateTime(m.timestamp).toMillis(),
                low: m.mean - zScore * (m.standardError ?? 0),
                high: m.mean + zScore * (m.standardError ?? 0),
                // use standardError as name to show in tooltip
                name:
                  m.standardError || m.standardError === 0
                    ? `${roundToTwoDecimals(zScore * m.standardError)}`
                    : '-',
              })),
              lineWidth: lineWidth,
              linkedTo: seriesId,
              zIndex: Z_INDEX.base.chart,
              tooltip: {
                ...defaultTooltipOptions,
                xDateFormat: hasApiDayBuckets(resolution)
                  ? defaultChartDateFormat
                  : defaultChartDateTimeFormat,
                pointFormatter: function () {
                  return `${t('linear.CI')}: <strong>${this.name !== undefined ? this.name : ''}</strong> ${unit ?? ''}<br>`;
                },
              },
              marker: {
                enabled: false,
                states: { hover: { enabled: false } },
              },
              showInLegend: true,
            });

            // Standard error bar chart
            chart?.addSeries({
              name: t('chartConfig:linear.standardError'),
              type: 'errorbar',
              color: colors.stroke.alarm.solid[100],
              data: plotMeasurements.map(m => ({
                x: fromApiDateTime(m.timestamp).toMillis(),
                low: m.mean - (m.standardError ?? 0),
                high: m.mean + (m.standardError ?? 0),
                // use standardError as name to show in tooltip
                name:
                  m.standardError || m.standardError === 0 ? `${round(m.standardError, 3)}` : '-',
              })),
              lineWidth: errorBarLineWidth,
              whiskerLength: '10%',
              linkedTo: seriesId,
              zIndex: Z_INDEX.base.chart,
              tooltip: {
                ...defaultTooltipOptions,
                xDateFormat: hasApiDayBuckets(resolution)
                  ? defaultChartDateFormat
                  : defaultChartDateTimeFormat,
                pointFormatter: function () {
                  return `${t('chartConfig:linear.standardError')}: <strong>${this.name !== undefined ? this.name : ''}</strong> ${unit ?? ''}<br>`;
                },
              },
              showInLegend: true,
              legendSymbol: 'lineMarker',
            });
          }
        })
      );

      const { min: xAxisMin, max: xAxisMax } = getAggregatedChartXAxisWindow({
        interval,
        isLive,
        isTimeSeriesNormalized,
        hasApiDayBuckets: hasApiDayBuckets(resolution),
        from,
        to,
      });

      applyBaseRange({ min: xAxisMin, max: xAxisMax });
    }

    // Redraw chart to ensure locale changes are applied to existing chart instance
    chart?.redraw();
  }, [
    i18n.language,
    interval,
    hasMeasurements,
    plot,
    allMeasurements,
    reference,
    t,
    units,
    sensors,
    sensors.length,
    plot.length,
    isLive,
    isTimeSeriesNormalized,
    from,
    to,
    resolution,
    chartColors,
    hiddenPlotTypes,
    tickInterval,
    minorTickInterval,
    minRange,
    ceiling,
    floor,
    maxPadding,
    lowDataCapturePoints,
    highChartsRef,
    getFriendlyProviderName,
    applyBaseRange,
  ]);
};
